// src/components/messaging/ConversationList.tsx
import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  List,
  CircularProgress,
  IconButton,
  Tooltip,
  Badge,
  alpha,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import ChatIcon from '@mui/icons-material/Chat';
import GroupsIcon from '@mui/icons-material/Groups';
import { useMessaging } from '../../contexts/MessagingContext';
import { useTranslation } from 'react-i18next';
import ConversationItem from './ConversationItem';

interface ConversationListProps {
  onNewConversation: () => void;
  onNewGroup: () => void;
}

const ConversationList: React.FC<ConversationListProps> = ({
  onNewConversation,
  onNewGroup,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const {
    state,
    fetchConversations,
    fetchGroups,
    selectConversation,
    selectGroup,
    setActiveTab,
  } = useMessaging();

  const {
    conversations,
    groups,
    currentConversation,
    currentGroup,
    activeTab,
    isLoadingConversations,
    isLoadingGroups,
    onlineUsers,
    unreadCount,
    totalGroupUnread,
    wsConnected,
  } = state;

  const [searchQuery, setSearchQuery] = useState('');

  // Fetch data on mount
  useEffect(() => {
    fetchConversations();
    fetchGroups();
  }, [fetchConversations, fetchGroups]);

  // Handle tab change
  const handleTabChange = (_event: React.SyntheticEvent, newValue: 'private' | 'groups') => {
    setActiveTab(newValue);
  };

  // Filter conversations/groups by search
  const filteredConversations = conversations.filter((conv) =>
    conv.other_user?.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredGroups = groups.filter((group) =>
    group.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Sort by last message time
  const sortedConversations = [...filteredConversations].sort((a, b) => {
    const aTime = a.last_message?.created_at || a.updated_at;
    const bTime = b.last_message?.created_at || b.updated_at;
    return new Date(bTime).getTime() - new Date(aTime).getTime();
  });

  const sortedGroups = [...filteredGroups].sort((a, b) => {
    const aTime = a.last_message?.created_at || a.updated_at;
    const bTime = b.last_message?.created_at || b.updated_at;
    return new Date(bTime).getTime() - new Date(aTime).getTime();
  });

  const isLoading = activeTab === 'private' ? isLoadingConversations : isLoadingGroups;
  const isEmpty = activeTab === 'private' ? sortedConversations.length === 0 : sortedGroups.length === 0;

  return (
    <Box
      sx={{
        width: isMobile ? '100%' : 350,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRight: isMobile ? 'none' : '1px solid',
        borderColor: 'grey.200',
        backgroundColor: 'background.paper',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          borderBottom: '1px solid',
          borderColor: 'grey.200',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {t('messaging.title', 'Messages')}
            </Typography>
            {/* WebSocket connection indicator */}
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: wsConnected ? '#10b981' : '#ef4444',
                boxShadow: wsConnected ? '0 0 6px #10b981' : '0 0 6px #ef4444',
              }}
              title={wsConnected ? 'Connecté' : 'Déconnecté'}
            />
          </Box>
          <Box>
            <Tooltip title={t('messaging.newMessage', 'Nouveau message')}>
              <IconButton
                onClick={onNewConversation}
                size="small"
                sx={{
                  backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
                  mr: 1,
                  '&:hover': {
                    backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.2),
                  },
                }}
              >
                <AddIcon color="primary" />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('messaging.createGroup', 'Créer un groupe')}>
              <IconButton
                onClick={onNewGroup}
                size="small"
                sx={{
                  backgroundColor: (theme) => alpha(theme.palette.secondary.main, 0.1),
                  '&:hover': {
                    backgroundColor: (theme) => alpha(theme.palette.secondary.main, 0.2),
                  },
                }}
              >
                <GroupAddIcon color="secondary" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Search */}
        <TextField
          fullWidth
          size="small"
          placeholder={t('messaging.search', 'Rechercher...')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: (theme) => alpha(theme.palette.grey[500], 0.08),
              '& fieldset': {
                borderColor: 'transparent',
              },
              '&:hover fieldset': {
                borderColor: 'grey.300',
              },
              '&.Mui-focused fieldset': {
                borderColor: 'primary.main',
              },
            },
          }}
        />
      </Box>

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        variant="fullWidth"
        sx={{
          borderBottom: '1px solid',
          borderColor: 'grey.200',
          minHeight: 48,
          '& .MuiTab-root': {
            minHeight: 48,
          },
        }}
      >
        <Tab
          value="private"
          label={
            <Badge
              badgeContent={unreadCount}
              color="primary"
              max={99}
              sx={{ '& .MuiBadge-badge': { right: -12, top: 0 } }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ChatIcon fontSize="small" />
                <span>{t('messaging.private', 'Privé')}</span>
              </Box>
            </Badge>
          }
        />
        <Tab
          value="groups"
          label={
            <Badge
              badgeContent={totalGroupUnread}
              color="secondary"
              max={99}
              sx={{ '& .MuiBadge-badge': { right: -12, top: 0 } }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <GroupsIcon fontSize="small" />
                <span>{t('messaging.groups', 'Groupes')}</span>
              </Box>
            </Badge>
          }
        />
      </Tabs>

      {/* List */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          py: 1,
        }}
      >
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
            <CircularProgress size={32} />
          </Box>
        ) : isEmpty ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: 200,
              color: 'text.secondary',
            }}
          >
            {activeTab === 'private' ? (
              <>
                <ChatIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
                <Typography variant="body2">
                  {t('messaging.noConversations', 'Aucune conversation')}
                </Typography>
              </>
            ) : (
              <>
                <GroupsIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
                <Typography variant="body2">
                  {t('messaging.noGroups', 'Aucun groupe')}
                </Typography>
              </>
            )}
          </Box>
        ) : (
          <List disablePadding>
            {activeTab === 'private'
              ? sortedConversations.map((conversation) => (
                  <ConversationItem
                    key={conversation.id}
                    conversation={conversation}
                    isSelected={currentConversation?.id === conversation.id}
                    isOnline={onlineUsers.has(conversation.other_user?.id || 0)}
                    onClick={() => selectConversation(conversation)}
                  />
                ))
              : sortedGroups.map((group) => (
                  <ConversationItem
                    key={group.id}
                    group={group}
                    isSelected={currentGroup?.id === group.id}
                    onClick={() => selectGroup(group)}
                  />
                ))}
          </List>
        )}
      </Box>
    </Box>
  );
};

export default ConversationList;
